import { ParsedRecord } from 'src/components/import/ImportParser';
import { isIntegerNumber, isNumber, numberInRange, numberIsPositive } from './index';
import { MAX_GENERATORS_AMOUNT } from '..';

export const pjmLocationBulkUpdateCsvConfig = [
    {
        column: 'Location ID*',
        required: true,
        description: 'The ID of the location to update',
        width: 300,
    },
    // Registration Input
    {
        column: 'Peak Load Contribution (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Winter Peak Load (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Capacity Loss Factor',
        required: false,
        description: '',
    },
    {
        column: 'Energy Loss Factor',
        required: false,
        description: '',
    },
    {
        column: 'Max Load (kW)',
        required: false,
        description:
            'The Max Load value must be equal to or greater than the Maximum Load Reduction Capability (Sum of all Reduction Methods: Manufacturing, HVAC, Generator, Batteries, Lighting, Water Heaters, Refrigeration, Plug Load, Computing)',
    },
    // Load Reduction Method
    {
        column: 'Manufacturing (kW)',
        required: false,
        description: 'This and below Reduction Method fields are not all required, but at least one is required!',
    },
    {
        column: 'HVAC (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Generator (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Number of Generators',
        required: false,
        description: '',
    },
    {
        column: 'Batteries (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Lighting (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Water Heaters (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Refrigeration (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Plug Load (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Computing (kW)',
        required: false,
        description: '',
    },
] as const;

export type PjmLocationBulkUpdateCsvColumns = (typeof pjmLocationBulkUpdateCsvConfig)[number]['column'];

export function pjmLocationBulkUpdateParser(row: ParsedRecord<PjmLocationBulkUpdateCsvColumns>): ParsedRecord<PjmLocationBulkUpdateCsvColumns> {
    row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Location ID*');

    // Registration Input
    if (row['Peak Load Contribution (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Peak Load Contribution (kW)', numberInRange(0, 9999999.999));
    }
    if (row['Winter Peak Load (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Winter Peak Load (kW)', numberInRange(0, 9999999.999));
    }
    if (row['Capacity Loss Factor'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Capacity Loss Factor', numberInRange(0, 1.99999));
    }
    if (row['Energy Loss Factor'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Energy Loss Factor', numberInRange(0, 1.99999));
    }
    if (row['Max Load (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Max Load (kW)', numberInRange(0, 9999999.999));
    }

    // Load Reduction Method
    if (row['Manufacturing (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Manufacturing (kW)', numberInRange(0, 999999.999));
    }
    if (row['HVAC (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'HVAC (kW)', numberInRange(0, 999999.999));
    }
    if (row['Generator (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Generator (kW)', numberInRange(0, 500000));
    }
    if (row['Number of Generators'].value !== '') {
        row = isIntegerNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Number of Generators', numberIsPositive());
    }
    if (row['Batteries (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Batteries (kW)', numberInRange(0, 500000));
    }
    if (row['Lighting (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Lighting (kW)', numberInRange(0, 999999.999));
    }
    if (row['Water Heaters (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Water Heaters (kW)', numberInRange(0, 999999.999));
    }
    if (row['Refrigeration (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Refrigeration (kW)', numberInRange(0, 999999.999));
    }
    if (row['Plug Load (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Plug Load (kW)', numberInRange(0, 999999.999));
    }
    if (row['Computing (kW)'].value !== '') {
        row = isNumber<PjmLocationBulkUpdateCsvColumns>(row, 'Computing (kW)', numberInRange(0, 999999.999));
    }

    if (row['Number of Generators'].parsedValue === 0 && row['Generator (kW)'].parsedValue > 0) {
        row['Number of Generators'].error = 'When Generator (kW) is provided, this value cannot be zero';
    }
    if (row['Number of Generators'].parsedValue > 0 && row['Generator (kW)'].parsedValue === 0) {
        row['Number of Generators'].error = 'When Generator (kW) is zero, this value must be zero too';
    }
    if (row['Number of Generators'].parsedValue > MAX_GENERATORS_AMOUNT) {
        row['Number of Generators'].error = 'Cannot have more than 10 generators';
    }

    return row;
}
